/* eslint-disable */
import { Dialog, Transition } from '@headlessui/react';
import axios from 'axios';
import moment from 'moment-timezone';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { Notification } from './PassNotifications';

const HOURS = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
const MINUTES = Array.from({ length: 60 }, (_, i) => `${i.toString().padStart(2, '0')}`);

const TIME_AFTER_UNITS = [
  { value: 'instant', label: 'Instant', min: 0, max: 0 },
  { value: 'hours', label: 'Hours', min: 1, max: 24 },
  { value: 'days', label: 'Days', min: 1, max: 30 },
  { value: 'weeks', label: 'Weeks', min: 1, max: 30 },
];

// Major timezones organized by region (similar to Windows 11)
const MAJOR_TIMEZONES = [
  // UTC
  'UTC',

  // North America
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',

  // South America
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Santiago',
  'America/Lima',
  'America/Bogota',

  // Europe
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Helsinki',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Budapest',
  'Europe/Bucharest',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',

  // Africa
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Casablanca',
  'Africa/Algiers',

  // Asia
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Seoul',
  'Asia/Beijing',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Bangkok',
  'Asia/Manila',
  'Asia/Jakarta',
  'Asia/Kuala_Lumpur',
  'Asia/Ho_Chi_Minh',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Karachi',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Tehran',
  'Asia/Jerusalem',
  'Asia/Baghdad',
  'Asia/Almaty',
  'Asia/Tashkent',
  'Asia/Yekaterinburg',
  'Asia/Novosibirsk',
  'Asia/Vladivostok',

  // Oceania
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Guam',
  'Pacific/Honolulu',
];

interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
  region: string;
}

interface TimezoneDropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const TimezoneDropdown = ({
  value,
  onChange,
  placeholder = 'Select timezone',
  className = '',
}: TimezoneDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const timezoneOptions = useMemo(() => {
    const allTimezones = moment.tz.names().map(tz => {
      const offset = moment.tz(tz).format('Z');
      const label = `(GMT${offset}) ${tz.replace('_', ' ')}`;

      // Determine region based on timezone name
      let region = 'Other';
      if (tz === 'UTC') region = 'UTC';
      else if (tz.startsWith('America/')) region = 'Americas';
      else if (tz.startsWith('Europe/')) region = 'Europe';
      else if (tz.startsWith('Asia/')) region = 'Asia';
      else if (tz.startsWith('Africa/')) region = 'Africa';
      else if (tz.startsWith('Australia/') || tz.startsWith('Pacific/'))
        region = 'Oceania';

      return { value: tz, label, offset, region };
    });

    // Sort: major timezones first (in order), then others alphabetically
    return allTimezones.sort((a, b) => {
      const aIndex = MAJOR_TIMEZONES.indexOf(a.value);
      const bIndex = MAJOR_TIMEZONES.indexOf(b.value);

      // If both are major timezones, sort by their order in MAJOR_TIMEZONES
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one is major, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // If neither is major, sort by region then alphabetically
      if (a.region !== b.region) {
        return a.region.localeCompare(b.region);
      }

      return a.label.localeCompare(b.label);
    });
  }, []);

  const filteredOptions = timezoneOptions.filter(
    option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = timezoneOptions.find(option => option.value === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-left focus:outline-none focus:ring-2 focus:ring-blue-200 transition bg-white"
      >
        {selectedOption ? selectedOption.label : placeholder}
        <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search timezones..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-gray-500">No timezones found</div>
            ) : (
              filteredOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${
                    option.value === value ? 'bg-blue-100 text-blue-700' : ''
                  } ${MAJOR_TIMEZONES.includes(option.value) ? 'font-medium' : ''}`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface CreateScheduleNotificationProps {
  open: boolean;
  onClose: () => void;
  advertId: string;
  onSuccess?: () => void;
  editNotification?: Notification | null;
  viewOnly?: boolean;
}

const CreateScheduleNotification = ({
  open,
  onClose,
  advertId,
  onSuccess,
  editNotification,
  viewOnly = false,
}: CreateScheduleNotificationProps) => {
  const [headline, setHeadline] = useState('');
  const [subHeading, setSubHeading] = useState('');
  const [triggerType, setTriggerType] = useState<'datetime' | 'after'>('datetime');
  const [date, setDate] = useState('');
  const [hour, setHour] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  // Time After Saving
  const [afterValue, setAfterValue] = useState(1);
  const [afterUnit, setAfterUnit] = useState('hours');
  // Expiration
  const [expireDate, setExpireDate] = useState('');
  const [expireTime, setExpireTime] = useState('');
  const [expireTimezone, setExpireTimezone] = useState('UTC');
  const cancelButtonRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Validation state
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Populate fields if editing
  useEffect(() => {
    if (editNotification && open) {
      setHeadline(editNotification.heading || '');
      setSubHeading(editNotification.subHeading || '');
      if (editNotification.launchTrigger === 'SPECIFIC_DATE_AND_TIME') {
        setTriggerType('datetime');
        if (editNotification.notificationSendTime) {
          const [d, t] = editNotification.notificationSendTime.split('T');
          setDate(d || '');
          setHour(t ? t.slice(0, 5) : '');
        }
        setTimezone(editNotification.notificationSendTimeZone || 'UTC');
      } else if (editNotification.launchTrigger === 'TIME_AFTER_SAVING') {
        setTriggerType('after');
        setAfterUnit(editNotification.timeAfterSavingUnit?.toLowerCase() || 'hours');
        setAfterValue(editNotification.timeAfterSavingValue ?? 1);
        if (editNotification.timeAfterSavingExpiry) {
          const [d, t] = editNotification.timeAfterSavingExpiry.split('T');
          setExpireDate(d || '');
          setExpireTime(t ? t.slice(0, 5) : '');
        } else {
          setExpireDate('');
          setExpireTime('');
        }
        setExpireTimezone(editNotification.timeAfterSavingExpiryTimeZone || 'UTC');
      }
    } else if (open) {
      // Reset fields if creating new
      setHeadline('');
      setSubHeading('');
      setTriggerType('datetime');
      setDate('');
      setHour('');
      setTimezone('UTC');
      setAfterValue(1);
      setAfterUnit('hours');
      setExpireDate('');
      setExpireTime('');
      setExpireTimezone('UTC');
    }
  }, [editNotification, open]);

  // Get min/max for afterValue based on afterUnit
  const afterUnitObj =
    TIME_AFTER_UNITS.find(u => u.value === afterUnit) || TIME_AFTER_UNITS[0];

  // Validation function
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Required fields for all notification types
    if (!headline.trim()) {
      newErrors.headline = 'Headline is required';
    }
    if (!subHeading.trim()) {
      newErrors.subHeading = 'Sub-heading is required';
    }

    if (triggerType === 'datetime') {
      // Specific Date & Time validation
      if (!date) {
        newErrors.date = 'Date is required';
      }
      if (!hour) {
        newErrors.hour = 'Time is required';
      }
      if (!timezone) {
        newErrors.timezone = 'Timezone is required';
      }
    } else {
      // Time After Saving validation
      if (afterUnit === 'instant') {
        // For instant, value should always be 0
        if (afterValue !== 0) {
          newErrors.afterValue = 'Value must be 0 for Instant';
        }
      } else {
        // For other units, check min/max
        if (
          !afterValue ||
          afterValue < afterUnitObj.min ||
          afterValue > afterUnitObj.max
        ) {
          newErrors.afterValue = `Value must be between ${afterUnitObj.min} and ${afterUnitObj.max}`;
        }
      }
      if (!afterUnit) {
        newErrors.afterUnit = 'Time unit is required';
      }

      // Expiration is completely optional - no validation needed
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid for button state
  const isFormValid = () => {
    if (!headline.trim() || !subHeading.trim()) return false;

    if (triggerType === 'datetime') {
      return !!(date && hour && timezone);
    } else {
      const hasValidAfterValue =
        afterUnit === 'instant'
          ? afterValue === 0
          : afterValue >= afterUnitObj.min && afterValue <= afterUnitObj.max;
      const hasValidAfterUnit = !!afterUnit;

      // Expiration is completely optional - no validation needed
      return hasValidAfterValue && hasValidAfterUnit;
    }
  };

  const scheduleHandler = async () => {
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    let url = `${
      process.env.REACT_APP_GATEWAY_URL || ''
    }/wallet-notifications/${advertId}`;
    let method: 'post' | 'patch' = 'post';
    let payload: any = {
      advertId,
      heading: headline,
      subHeading: subHeading,
    };
    if (editNotification && editNotification.id) {
      // PATCH for edit mode, use new API contract
      method = 'patch';
      payload = {
        walletNotificationId: editNotification.id,
        heading: headline,
        subHeading: subHeading,
        notificationSendTime: '',
        notificationSendTimeZone: '',
        notificationSendTimeInUTC: '',
        notificationStatus: 'SCHEDULED',
      };
      if (triggerType === 'datetime') {
        if (!date || !hour || !timezone) return;
        const datetime = moment.tz(`${date} ${hour}`, 'YYYY-MM-DD HH:mm', timezone);
        payload.notificationSendTime = `${date}T${hour}:00`;
        payload.notificationSendTimeZone = timezone;
        payload.notificationSendTimeInUTC = datetime.utc().toISOString();
      }
      // Only send PATCH for SPECIFIC_DATE_AND_TIME as per contract
      try {
        await axios({
          method,
          url,
          data: payload,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        if (onSuccess) onSuccess();
        onClose();
      } catch (err) {
        alert('Failed to update notification.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (triggerType === 'datetime') {
      if (!date || !hour || !timezone) return;
      const datetime = moment.tz(`${date} ${hour}`, 'YYYY-MM-DD HH:mm', timezone);
      payload.launchTrigger = 'SPECIFIC_DATE_AND_TIME';
      payload.notificationSendTime = `${date}T${hour}:00`;
      payload.notificationSendTimeZone = timezone;
      payload.notificationSendTimeInUTC = datetime.utc().toISOString();
    } else {
      payload.launchTrigger = 'TIME_AFTER_SAVING';
      payload.timeAfterSavingUnit = afterUnit.toUpperCase(); // INSTANT | HOUR | DAY | WEEK
      payload.timeAfterSavingValue = afterValue;
      if (expireDate && expireTime && expireTimezone) {
        const expireMoment = moment.tz(
          `${expireDate} ${expireTime}`,
          'YYYY-MM-DD HH:mm',
          expireTimezone
        );
        payload.TimeAfterSavingExpiry = `${expireDate}T${expireTime}:00`;
        payload.TimeAfterSavingExpiryTimeZone = expireTimezone;
        payload.TimeAfterSavingExpiryInUTC = expireMoment.utc().toISOString();
      }
    }
    try {
      await axios({
        method,
        url,
        data: payload,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      alert('Failed to schedule notification.');
    } finally {
      setLoading(false);
    }
  };

  // Clear errors when form fields change
  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-tablet-page min-h-[70vh] flex flex-col transform overflow-hidden rounded-2xl bg-white p-0 text-left align-middle shadow-2xl transition-all border border-gray-100">
                {/* Header */}
                <div className="flex justify-between items-center px-8 pt-2 pb-2 bg-gradient-to-r from-blue-100/70 via-white to-blue-50 border-b border-gray-100 relative">
                  <div className="flex items-center gap-2">
                    <div className="bg-blue-500/10 rounded-full p-1 flex items-center justify-center">
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                        <path
                          d="M12 19a2 2 0 0 0 2-2H10a2 2 0 0 0 2 2Zm6-6V9a6 6 0 1 0-12 0v4a2 2 0 0 1-2 2v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1a2 2 0 0 1-2-2Z"
                          stroke="#2563eb"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <Dialog.Title
                      as="h5"
                      className="text-base font-semibold text-gray-800"
                    >
                      Schedule Push Notification
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    className="text-lg text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded transition p-1"
                    onClick={onClose}
                    ref={cancelButtonRef}
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </div>
                {/* Form Body */}
                <form className="flex flex-col gap-6 px-8 py-8 bg-white flex-grow">
                  <div>
                    <label
                      htmlFor="headline"
                      className="block font-semibold mb-1 text-gray-700"
                    >
                      Headline *
                    </label>
                    <input
                      id="headline"
                      type="text"
                      className={`border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 transition placeholder-gray-400 ${
                        errors.headline
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-300 focus:ring-blue-200'
                      }`}
                      value={headline}
                      onChange={e => {
                        setHeadline(e.target.value);
                        clearError('headline');
                      }}
                      placeholder="5K Before Friday? Let's Go 🏅"
                      disabled={viewOnly}
                    />
                    {errors.headline && (
                      <p className="text-red-500 text-sm mt-1">{errors.headline}</p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="subHeading"
                      className="block font-semibold mb-1 text-gray-700"
                    >
                      Sub-heading *
                    </label>
                    <input
                      id="subHeading"
                      type="text"
                      className={`border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 transition placeholder-gray-400 ${
                        errors.subHeading
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-300 focus:ring-blue-200'
                      }`}
                      value={subHeading}
                      onChange={e => {
                        setSubHeading(e.target.value);
                        clearError('subHeading');
                      }}
                      placeholder="Join the challenge. Track it in the app. Crush your week with Nike."
                      disabled={viewOnly}
                    />
                    {errors.subHeading && (
                      <p className="text-red-500 text-sm mt-1">{errors.subHeading}</p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="triggerTypeDatetime"
                      className="block font-semibold mb-2 text-gray-700"
                    >
                      Launch trigger *
                    </label>
                    <div className="flex gap-4 mb-6">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="triggerType"
                          value="datetime"
                          checked={triggerType === 'datetime'}
                          onChange={() => setTriggerType('datetime')}
                          disabled={!!editNotification || viewOnly}
                        />
                        <span>Specific Date & Time</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="triggerType"
                          value="after"
                          checked={triggerType === 'after'}
                          onChange={() => setTriggerType('after')}
                          disabled={!!editNotification || viewOnly}
                        />
                        <span>Time After Saving</span>
                      </label>
                    </div>
                    {triggerType === 'datetime' ? (
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row gap-4 items-center">
                          <div className="flex-1">
                            <input
                              type="date"
                              className={`border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 transition ${
                                errors.date
                                  ? 'border-red-300 focus:ring-red-200'
                                  : 'border-gray-300 focus:ring-blue-200'
                              }`}
                              value={date}
                              onChange={e => {
                                setDate(e.target.value);
                                clearError('date');
                              }}
                              disabled={viewOnly}
                            />
                            {errors.date && (
                              <p className="text-red-500 text-sm mt-1">{errors.date}</p>
                            )}
                          </div>
                          <div className="flex-1">
                            <select
                              className={`border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 transition ${
                                errors.hour
                                  ? 'border-red-300 focus:ring-red-200'
                                  : 'border-gray-300 focus:ring-blue-200'
                              }`}
                              value={hour}
                              onChange={e => {
                                setHour(e.target.value);
                                clearError('hour');
                              }}
                              disabled={viewOnly}
                            >
                              <option value="">--:--</option>
                              {HOURS.map(h => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                            </select>
                            {errors.hour && (
                              <p className="text-red-500 text-sm mt-1">{errors.hour}</p>
                            )}
                          </div>
                          <div className="flex-1">
                            {viewOnly ? (
                              <input
                                type="text"
                                className="border rounded-lg px-4 py-2 w-full bg-gray-100 text-gray-700 cursor-not-allowed"
                                value={timezone}
                                readOnly
                                tabIndex={-1}
                              />
                            ) : (
                              <TimezoneDropdown
                                value={timezone}
                                onChange={value => {
                                  setTimezone(value);
                                  clearError('timezone');
                                }}
                                placeholder="Select timezone"
                                className="w-full"
                              />
                            )}
                            {errors.timezone && (
                              <p className="text-red-500 text-sm mt-1">
                                {errors.timezone}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-4">
                          <div className="flex flex-col sm:flex-row gap-4 items-center">
                            <div className="flex-1">
                              <input
                                type="number"
                                min={afterUnitObj.min}
                                max={afterUnitObj.max}
                                disabled={afterUnit === 'instant' || viewOnly}
                                className={`border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 transition ${
                                  errors.afterValue
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-300 focus:ring-blue-200'
                                } ${
                                  afterUnit === 'instant'
                                    ? 'bg-gray-100 cursor-not-allowed'
                                    : ''
                                }`}
                                value={afterValue}
                                onChange={e => {
                                  let v = Number(e.target.value);
                                  if (v < afterUnitObj.min) v = afterUnitObj.min;
                                  if (v > afterUnitObj.max) v = afterUnitObj.max;
                                  setAfterValue(v);
                                  clearError('afterValue');
                                }}
                              />
                              {errors.afterValue && (
                                <p className="text-red-500 text-sm mt-1">
                                  {errors.afterValue}
                                </p>
                              )}
                            </div>
                            <div className="flex-1">
                              <select
                                className={`border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 transition ${
                                  errors.afterUnit
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-300 focus:ring-blue-200'
                                }`}
                                value={afterUnit}
                                onChange={e => {
                                  setAfterUnit(e.target.value);
                                  clearError('afterUnit');
                                  // Reset value to min of new unit, or 0 for instant
                                  if (e.target.value === 'instant') {
                                    setAfterValue(0);
                                  } else {
                                    const newUnit = TIME_AFTER_UNITS.find(
                                      u => u.value === e.target.value
                                    );
                                    setAfterValue(newUnit ? newUnit.min : 1);
                                  }
                                }}
                                disabled={viewOnly}
                              >
                                {TIME_AFTER_UNITS.map(u => (
                                  <option key={u.value} value={u.value}>
                                    {u.label}
                                  </option>
                                ))}
                              </select>
                              {errors.afterUnit && (
                                <p className="text-red-500 text-sm mt-1">
                                  {errors.afterUnit}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Expiration Section */}
                        <div className="mt-6">
                          <label className="block text-xl font-bold text-gray-800 mb-1">
                            Expiration (Optional)
                          </label>
                          <p className="text-gray-500 mb-3">
                            When would you like this notification to expire?
                          </p>
                          <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-4 items-center">
                              <div className="flex-1">
                                <input
                                  type="date"
                                  className={`border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 transition ${
                                    errors.expireDate
                                      ? 'border-red-300 focus:ring-red-200'
                                      : 'border-gray-300 focus:ring-blue-200'
                                  }`}
                                  value={expireDate}
                                  onChange={e => {
                                    setExpireDate(e.target.value);
                                    clearError('expireDate');
                                  }}
                                  disabled={viewOnly}
                                />
                                {errors.expireDate && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errors.expireDate}
                                  </p>
                                )}
                              </div>
                              <div className="flex-1">
                                <input
                                  type="time"
                                  className={`border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 transition ${
                                    errors.expireTime
                                      ? 'border-red-300 focus:ring-red-200'
                                      : 'border-gray-300 focus:ring-blue-200'
                                  }`}
                                  value={expireTime}
                                  onChange={e => {
                                    setExpireTime(e.target.value);
                                    clearError('expireTime');
                                  }}
                                  disabled={viewOnly}
                                />
                                {errors.expireTime && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errors.expireTime}
                                  </p>
                                )}
                              </div>
                              <div className="flex-1">
                                {viewOnly ? (
                                  <input
                                    type="text"
                                    className="border rounded-lg px-4 py-2 w-full bg-gray-100 text-gray-700 cursor-not-allowed"
                                    value={expireTimezone}
                                    readOnly
                                    tabIndex={-1}
                                  />
                                ) : (
                                  <TimezoneDropdown
                                    value={expireTimezone}
                                    onChange={value => {
                                      setExpireTimezone(value);
                                      clearError('expireTimezone');
                                    }}
                                    placeholder="Select timezone"
                                    className="w-full"
                                  />
                                )}
                                {errors.expireTimezone && (
                                  <p className="text-red-500 text-sm mt-1">
                                    {errors.expireTimezone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </form>
                {/* Footer - sticky to bottom */}
                <div className="flex justify-end gap-4 bg-gradient-to-r from-blue-50/60 to-white border-t border-gray-100 px-8 py-5 rounded-b-2xl">
                  <button
                    type="button"
                    className="bg-white border border-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
                    onClick={onClose}
                    disabled={viewOnly}
                  >
                    Cancel
                  </button>
                  {!viewOnly && (
                    <button
                      type="button"
                      className={`px-8 py-2 rounded-lg font-semibold shadow focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${
                        loading || !isFormValid()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      onClick={scheduleHandler}
                      disabled={loading || !isFormValid() || viewOnly}
                    >
                      Schedule
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CreateScheduleNotification;
